import {
  TextInput,
  Grid,
  Stack,
  ScrollArea,
  Button,
  Select,
  LoadingOverlay,
  Tabs,
  SimpleGrid,
} from "@mantine/core";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useMutation } from "@apollo/client";
import { showNotification } from "@mantine/notifications";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { ADMIN_UPDATE_BasicSupplierBusinessInfo } from "apollo/mutuations";
import { customLoader } from "components/utilities/loader";
import { IconUpload } from "@tabler/icons-react";

const AdminUpdateSupplierBusiness = ({ setOpened, data }) => {
  const form = useForm({
    initialValues: {
      business_name: "",
      business_type: "",
      owner_id_type: "",
      tin: "",
      number_of_warehouses: 3,
      business_registration_number: "",
    },
  });

  const [files, setFiles] = useState({
    owner_id: [],
    tin_certification_document: [],
    trade_license_document: [],
  });

  const [fileUrls, setFileUrls] = useState({
    owner_id: "",
    tin_certification_document: "",
    trade_license_document: "",
  });

  useEffect(() => {
    if (data) {
      form.setValues({
        business_name: data.business_name,
        business_type: data.business_type,
        owner_id_type: data.owner_id_type,
        tin: data.tin,
        number_of_warehouses: data.number_of_warehouses,
        business_registration_number: data.business_registration_number,
      });

      setFileUrls({
        owner_id: data.owner_id_url || "",
        tin_certification_document: data.tin_certification_document_url || "",
        trade_license_document: data.trade_license_document_url || "",
      });
    }
  }, [data]);

  const [updateSupplierBusiness, { loading: updateBussinessLoading }] =
    useMutation(ADMIN_UPDATE_BasicSupplierBusinessInfo);

  const { height } = useViewportSize();

  const submit = () => {
    let variables = {
      id: data.id,
      business_name: form.getInputProps("business_name").value,
      business_type: form.getInputProps("business_type").value,
      owner_id_type: form.getInputProps("owner_id_type").value,
      tin: form.getInputProps("tin").value,
      number_of_warehouses: parseInt(
        form.getInputProps("number_of_warehouses").value,
      ),
      business_registration_number: form.getInputProps(
        "business_registration_number",
      ).value,
      owner_id: files.owner_id[0],
      tin_certification_document: files.tin_certification_document[0],
      trade_license_document: files.trade_license_document[0],
    };

    updateSupplierBusiness({
      variables,
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Supplier Business Updated Successfully",
        });
        setOpened(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Update Business",
        });
      },
    });
  };

  const fileInputRefs = {
    owner_id: useRef(null),
    tin_certification_document: useRef(null),
    trade_license_document: useRef(null),
  };

  const handleFileChange = (event, fileType) => {
    const newFiles = Array.from(event.target.files);
    setFiles((prev) => ({
      ...prev,
      [fileType]: newFiles,
    }));
    if (newFiles.length) {
      setFileUrls((prev) => ({
        ...prev,
        [fileType]: "", // Clear old URL when new file is chosen
      }));
    }
  };

  const FilePreview = ({ fileList, defaultUrl }) => {
    const previews = useMemo(() => {
      const allFiles = [...fileList];
      if (!allFiles.length && !defaultUrl) return null;

      return allFiles.length
        ? allFiles.map((file, index) => {
            const fileUrl = URL.createObjectURL(file);
            return (
              <div key={index}>
                {file.type === "application/pdf" ? (
                  <embed
                    src={fileUrl}
                    width="130"
                    height="160"
                    type="application/pdf"
                    onLoad={() => URL.revokeObjectURL(fileUrl)}
                  />
                ) : (
                  <img
                    src={fileUrl}
                    alt=""
                    width="130"
                    onLoad={() => URL.revokeObjectURL(fileUrl)}
                  />
                )}
              </div>
            );
          })
        : [
            <div key="default">
              {defaultUrl.endsWith(".pdf") ? (
                <embed
                  src={defaultUrl}
                  width="130"
                  height="160"
                  type="application/pdf"
                />
              ) : (
                <img src={defaultUrl} alt="" width="130" />
              )}
            </div>,
          ];
    }, [fileList, defaultUrl]);

    return (
      <SimpleGrid
        cols={3}
        spacing="md"
        style={{ maxHeight: 200, overflowY: "auto" }}
      >
        {previews}
      </SimpleGrid>
    );
  };

  const [activeTab, setActiveTab] = useState("business-info");
  const tabList = ["business-info", "business-documents"];
  const nextTab = () => {
    const currentIndex = tabList.indexOf(activeTab);
    if (currentIndex < tabList.length - 1) {
      setActiveTab(tabList[currentIndex + 1]);
    }
  };
  const prevTab = () => {
    const currentIndex = tabList.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabList[currentIndex - 1]);
    }
  };

  return (
    <>
      <LoadingOverlay
        visible={updateBussinessLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())} noValidate>
          <Tabs
            value={activeTab}
            onTabChange={setActiveTab}
            styles={{
              tabList: {
                borderBottom: `2px solid #FF6A00`,
              },
              tab: {
                "&[data-active]": {
                  borderColor: "#FF6A00",
                },
                "&:hover": {
                  borderColor: "#FF6A00",
                },
              },
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="business-info">Business Information</Tabs.Tab>
              <Tabs.Tab value="business-documents">Business Documents</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="business-info">
              <Grid gutter="md">
                <Grid.Col span={4}>
                  <TextInput
                    required
                    label="Business Name"
                    placeholder="Business Name"
                    {...form.getInputProps("business_name")}
                  />
                </Grid.Col>

                <Grid.Col span={4}>
                  <Select
                    required
                    data={["MANUFACTURER", "WHOLESALER", "DISTRIBUTOR"]}
                    label="Business Type"
                    {...form.getInputProps("business_type")}
                  />
                </Grid.Col>

                <Grid.Col span={4}>
                  <TextInput
                    required
                    type="number"
                    label="Number of Warehouses"
                    placeholder="Number of Warehouses"
                    {...form.getInputProps("number_of_warehouses")}
                  />
                </Grid.Col>

                <Grid.Col span={4}>
                  <TextInput
                    required
                    label="TIN"
                    placeholder="Tax Identification Number"
                    {...form.getInputProps("tin")}
                  />
                </Grid.Col>

                <Grid.Col span={4}>
                  <TextInput
                    required
                    label="Business Registration Number"
                    placeholder="Business Registration Number"
                    {...form.getInputProps("business_registration_number")}
                  />
                </Grid.Col>

                <Grid.Col span={4}>
                  <Select
                    required
                    data={[
                      "KEBELE_ID",
                      "PASSPORT",
                      "NATIONAL_ID",
                      "DRVING_LICENSE",
                    ]}
                    label="Owner ID Type"
                    {...form.getInputProps("owner_id_type")}
                  />
                </Grid.Col>

                <Grid.Col span={4}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      marginTop: "10px",
                    }}
                  >
                    <Button
                      onClick={() => fileInputRefs.owner_id.current.click()}
                      variant="outline"
                      leftIcon={<IconUpload size={16} />}
                      style={{ flex: "0 0 auto" }}
                    >
                      Owner ID
                    </Button>

                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg,.pdf"
                      ref={fileInputRefs.owner_id}
                      style={{ display: "none" }}
                      onChange={(e) => handleFileChange(e, "owner_id")}
                    />

                    {/* Preview */}
                    <div style={{ flex: "1 1 auto" }}>
                      <FilePreview
                        fileList={files.owner_id}
                        defaultUrl={fileUrls.owner_id}
                      />
                    </div>
                  </div>
                </Grid.Col>
              </Grid>
            </Tabs.Panel>

            <Tabs.Panel value="business-documents">
              <Grid>
                <Grid.Col span={4}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      marginTop: "10px",
                    }}
                  >
                    <Button
                      onClick={() =>
                        fileInputRefs.tin_certification_document.current.click()
                      }
                      variant="outline"
                      leftIcon={<IconUpload size={16} />}
                      style={{ flex: "0 0 auto" }}
                    >
                      Tin Certificate
                    </Button>
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg,.pdf"
                      ref={fileInputRefs.tin_certification_document}
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleFileChange(e, "tin_certification_document")
                      }
                    />
                    <FilePreview
                      fileList={files.tin_certification_document}
                      defaultUrl={fileUrls.tin_certification_document}
                    />
                  </div>
                </Grid.Col>

                <Grid.Col span={4}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "20px",
                      marginTop: "10px",
                    }}
                  >
                    <Button
                      onClick={() =>
                        fileInputRefs.trade_license_document.current.click()
                      }
                      variant="outline"
                      leftIcon={<IconUpload size={16} />}
                      style={{ flex: "0 0 auto" }}
                    >
                      Trade License
                    </Button>
                    <input
                      type="file"
                      accept="image/png, image/jpeg, image/jpg,.pdf"
                      ref={fileInputRefs.trade_license_document}
                      style={{ display: "none" }}
                      onChange={(e) =>
                        handleFileChange(e, "trade_license_document")
                      }
                    />
                    <FilePreview
                      fileList={files.trade_license_document}
                      defaultUrl={fileUrls.trade_license_document}
                    />
                  </div>
                </Grid.Col>
              </Grid>
            </Tabs.Panel>
          </Tabs>

          <Grid>
            <Grid.Col span={12}>
              <Button
                onClick={prevTab}
                style={{
                  marginTop: "10px",
                  backgroundColor: "#FF6A00",
                  color: "#FFFFFF",
                }}
                disabled={activeTab === "business-info"}
              >
                Previous
              </Button>
              {activeTab !== "business-documents" && (
                <Button
                  style={{
                    marginLeft: "14px",
                    marginTop: "10px",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  onClick={nextTab}
                >
                  Next
                </Button>
              )}
              {activeTab === "business-documents" && (
                <Button
                  style={{
                    marginLeft: "10px",
                    marginTop: "10px",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  onClick={submit}
                  loading={updateBussinessLoading}
                >
                  Submit
                </Button>
              )}
            </Grid.Col>
          </Grid>
        </form>
      </ScrollArea>
    </>
  );
};

export default AdminUpdateSupplierBusiness;
